<template>
	<div class="stats-detail">
		<el-row :gutter="20">
			<el-col :span="24">
				<div class="chart-card">
					<div class="chart-header">
						<span class="chart-title">操作统计对比</span>
					</div>
					<div class="chart-content">
						<div ref="barChart" class="echart-container"></div>
					</div>
				</div>
			</el-col>
		</el-row>
		
		<el-row :gutter="20" style="margin-top: 20px;">
			<el-col :span="12">
				<div class="chart-card">
					<div class="chart-header">
						<span class="chart-title">下架原因占比</span>
					</div>
					<div class="chart-content">
						<div ref="pulloffPieChart" class="echart-container"></div>
					</div>
				</div>
			</el-col>
			<el-col :span="12">
				<div class="chart-card">
					<div class="chart-header">
						<span class="chart-title">删除原因占比</span>
					</div>
					<div class="chart-content">
						<div ref="deletePieChart" class="echart-container"></div>
					</div>
				</div>
			</el-col>
		</el-row>
		
		<el-row :gutter="20" style="margin-top: 20px;">
			<el-col :span="24">
				<div class="chart-card">
					<div class="chart-header">
						<span class="chart-title">近七日趋势</span>
					</div>
					<div class="chart-content">
						<div ref="trendChart" class="echart-container"></div>
					</div>
				</div>
			</el-col>
		</el-row>
	</div>
</template>

<script>
import * as echarts from 'echarts';

export default {
	name: 'StatsDetail',
	props: {
		statsData: {
			type: Object,
			default: () => ({})
		},
		trendData: {
			type: Object,
			default: () => ({})
		}
	},
	data() {
		return {
			barChart: null,
			pulloffPieChart: null,
			deletePieChart: null,
			trendChart: null,
			pieColors: ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4']
		};
	},
	watch: {
		statsData: {
			handler() {
				this.$nextTick(() => {
					this.initCharts();
				});
			},
			deep: true
		},
		trendData: {
			handler() {
				this.$nextTick(() => {
					this.initTrendChart();
				});
			},
			deep: true
		}
	},
	mounted() {
		this.$nextTick(() => {
			this.initCharts();
		});
		window.addEventListener('resize', this.handleResize);
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.handleResize);
		this.disposeCharts();
	},
	methods: {
		initCharts() {
			this.initBarChart();
			this.initPulloffPieChart();
			this.initDeletePieChart();
			this.initTrendChart();
		},
		handleResize() {
			this.barChart && this.barChart.resize();
			this.pulloffPieChart && this.pulloffPieChart.resize();
			this.deletePieChart && this.deletePieChart.resize();
			this.trendChart && this.trendChart.resize();
		},
		disposeCharts() {
			this.barChart && this.barChart.dispose();
			this.pulloffPieChart && this.pulloffPieChart.dispose();
			this.deletePieChart && this.deletePieChart.dispose();
			this.trendChart && this.trendChart.dispose();
		},
		initBarChart() {
			if (!this.$refs.barChart) return;
			
			this.barChart = echarts.init(this.$refs.barChart);
			
			const option = {
				tooltip: {
					trigger: 'axis',
					axisPointer: {
						type: 'shadow'
					}
				},
				grid: {
					left: '3%',
					right: '4%',
					bottom: '3%',
					containLabel: true
				},
				xAxis: {
					type: 'category',
					data: ['发布', '下架', '删除'],
					axisLine: {
						lineStyle: {
							color: '#999'
						}
					},
					axisLabel: {
						color: '#666',
						fontSize: 14
					}
				},
				yAxis: {
					type: 'value',
					axisLine: {
						lineStyle: {
							color: '#999'
						}
					},
					axisLabel: {
						color: '#666'
					},
					splitLine: {
						lineStyle: {
							color: '#f0f0f0'
						}
					}
				},
				series: [
					{
						type: 'bar',
						data: [
							{
								value: this.statsData.total_publish || 0,
								itemStyle: { color: '#667eea' }
							},
							{
								value: this.statsData.total_pulloff || 0,
								itemStyle: { color: '#f5576c' }
							},
							{
								value: this.statsData.total_delete || 0,
								itemStyle: { color: '#4facfe' }
							}
						],
						barWidth: '40%',
						label: {
							show: true,
							position: 'top',
							color: '#333',
							fontSize: 14,
							fontWeight: 600
						}
					}
				]
			};
			
			this.barChart.setOption(option);
		},
		initPulloffPieChart() {
			if (!this.$refs.pulloffPieChart) return;
			
			this.pulloffPieChart = echarts.init(this.$refs.pulloffPieChart);
			
			const pieData = this.formatPieData(this.statsData.pulloff_reasons);
			
			const option = {
				tooltip: {
					trigger: 'item',
					formatter: '{b}: {c} ({d}%)'
				},
				legend: {
					orient: 'vertical',
					right: 10,
					top: 'center',
					textStyle: {
						color: '#666',
						fontSize: 12
					}
				},
				series: [
					{
						type: 'pie',
						radius: ['40%', '70%'],
						center: ['40%', '50%'],
						avoidLabelOverlap: false,
						itemStyle: {
							borderRadius: 10,
							borderColor: '#fff',
							borderWidth: 2
						},
						label: {
							show: false,
							position: 'center'
						},
						emphasis: {
							label: {
								show: true,
								fontSize: 16,
								fontWeight: 'bold'
							}
						},
						labelLine: {
							show: false
						},
						data: pieData
					}
				]
			};
			
			this.pulloffPieChart.setOption(option);
		},
		initDeletePieChart() {
			if (!this.$refs.deletePieChart) return;
			
			this.deletePieChart = echarts.init(this.$refs.deletePieChart);
			
			const pieData = this.formatPieData(this.statsData.delete_reasons);
			
			const option = {
				tooltip: {
					trigger: 'item',
					formatter: '{b}: {c} ({d}%)'
				},
				legend: {
					orient: 'vertical',
					right: 10,
					top: 'center',
					textStyle: {
						color: '#666',
						fontSize: 12
					}
				},
				series: [
					{
						type: 'pie',
						radius: ['40%', '70%'],
						center: ['40%', '50%'],
						avoidLabelOverlap: false,
						itemStyle: {
							borderRadius: 10,
							borderColor: '#fff',
							borderWidth: 2
						},
						label: {
							show: false,
							position: 'center'
						},
						emphasis: {
							label: {
								show: true,
								fontSize: 16,
								fontWeight: 'bold'
							}
						},
						labelLine: {
							show: false
						},
						data: pieData
					}
				]
			};
			
			this.deletePieChart.setOption(option);
		},
		initTrendChart() {
			if (!this.$refs.trendChart) return;
			
			this.trendChart = echarts.init(this.$refs.trendChart);
			
			const dates = this.trendData.dates || this.getLast7Days();
			const publishData = this.trendData.publish || [];
			const pulloffData = this.trendData.pulloff || [];
			const deleteData = this.trendData.delete || [];
			
			const option = {
				tooltip: {
					trigger: 'axis'
				},
				legend: {
					data: ['发布', '下架', '删除'],
					textStyle: {
						color: '#666'
					}
				},
				grid: {
					left: '3%',
					right: '4%',
					bottom: '3%',
					containLabel: true
				},
				xAxis: {
					type: 'category',
					boundaryGap: false,
					data: dates,
					axisLine: {
						lineStyle: {
							color: '#999'
						}
					},
					axisLabel: {
						color: '#666'
					}
				},
				yAxis: {
					type: 'value',
					axisLine: {
						lineStyle: {
							color: '#999'
						}
					},
					axisLabel: {
						color: '#666'
					},
					splitLine: {
						lineStyle: {
							color: '#f0f0f0'
						}
					}
				},
				series: [
					{
						name: '发布',
						type: 'line',
						smooth: true,
						data: publishData,
						itemStyle: {
							color: '#667eea'
						},
						areaStyle: {
							color: {
								type: 'linear',
								x: 0, y: 0, x2: 0, y2: 1,
								colorStops: [
									{ offset: 0, color: 'rgba(102, 126, 234, 0.3)' },
									{ offset: 1, color: 'rgba(102, 126, 234, 0.05)' }
								]
							}
						}
					},
					{
						name: '下架',
						type: 'line',
						smooth: true,
						data: pulloffData,
						itemStyle: {
							color: '#f5576c'
						},
						areaStyle: {
							color: {
								type: 'linear',
								x: 0, y: 0, x2: 0, y2: 1,
								colorStops: [
									{ offset: 0, color: 'rgba(245, 87, 108, 0.3)' },
									{ offset: 1, color: 'rgba(245, 87, 108, 0.05)' }
								]
							}
						}
					},
					{
						name: '删除',
						type: 'line',
						smooth: true,
						data: deleteData,
						itemStyle: {
							color: '#4facfe'
						},
						areaStyle: {
							color: {
								type: 'linear',
								x: 0, y: 0, x2: 0, y2: 1,
								colorStops: [
									{ offset: 0, color: 'rgba(79, 172, 254, 0.3)' },
									{ offset: 1, color: 'rgba(79, 172, 254, 0.05)' }
								]
							}
						}
					}
				]
			};
			
			this.trendChart.setOption(option);
		},
		formatPieData(reasons) {
			if (!reasons || Object.keys(reasons).length === 0) {
				return [{ value: 0, name: '暂无数据' }];
			}
			
			return Object.entries(reasons).map(([name, value], index) => ({
				name,
				value,
				itemStyle: {
					color: this.pieColors[index % this.pieColors.length]
				}
			}));
		},
		getLast7Days() {
			const dates = [];
			for (let i = 6; i >= 0; i--) {
				const date = new Date();
				date.setDate(date.getDate() - i);
				dates.push((date.getMonth() + 1) + '-' + date.getDate());
			}
			return dates;
		}
	}
};
</script>

<style scoped>
.stats-detail {
	padding: 20px;
}

.chart-card {
	background: #fff;
	border-radius: 8px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
	overflow: hidden;
}

.chart-header {
	padding: 16px 20px;
	border-bottom: 1px solid #f0f0f0;
	background: #fafafa;
}

.chart-title {
	font-size: 16px;
	font-weight: 600;
	color: #333;
}

.chart-content {
	padding: 20px;
}

.echart-container {
	height: 300px;
	width: 100%;
}

@media (max-width: 768px) {
	.el-col {
		margin-bottom: 20px;
	}
	
	.echart-container {
		height: 250px;
	}
}
</style>
